import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import { AUDIT_ACTION_TYPES, exportAuditLogCsv, filterAuditEvents } from '../../utils/auditLog';
import { formatDateTime } from '../../utils/transcriptionMeta';

const COLUMN_DEFS = [
  { key: 'created_at', label: 'Time' },
  { key: 'action_type', label: 'Action' },
  { key: 'actor_name', label: 'Actor' },
  { key: 'resource', label: 'Resource' },
  { key: 'details', label: 'Details' },
];

const AdminAuditLog = () => {
  const { user } = useAuth();
  const { dark } = useTheme();
  const tenantId = user?.tenant_id;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [actionType, setActionType] = useState('all');
  const [actor, setActor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await adminAPI.getAuditLog({ tenant_id: tenantId });
        setEvents(resp.data?.data || resp.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  const filtered = useMemo(
    () =>
      filterAuditEvents(events, {
        query,
        action_type: actionType,
        actor,
        date_from: dateFrom,
        date_to: dateTo,
      }),
    [events, query, actionType, actor, dateFrom, dateTo]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);
  const show = (key) => visibleKeys.includes(key);
  const activeFilterCount = [
    actionType !== 'all',
    Boolean(actor.trim()),
  ].filter(Boolean).length;

  const inputCls = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const searchCls = `${inputCls} w-full`;
  const dateCls = `${inputCls} w-[150px] shrink-0`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-20 hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setActionType('all');
    setActor('');
    setPage(1);
  };

  const toggleColumn = (key) => {
    setVisibleKeys((keys) => (
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    ));
  };

  const exportCsv = () => {
    exportAuditLogCsv(filtered, 'court-audit-log.csv');
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-audit-log.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  return (
    <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
      <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
        <div className="relative w-full max-w-sm min-w-[180px] shrink">
          <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search action, actor, resource..."
            className={`${searchCls} pl-9 h-9 py-1.5`}
          />
        </div>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className={`${dateCls} h-9 py-1.5`}
          aria-label="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className={`${dateCls} h-9 py-1.5`}
          aria-label="To date"
        />

        <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Filters">
              <FunnelIcon className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className={dropdownPanel}>
              <div className={dropdownInner}>
                <div>
                  <label className="block text-xs font-medium mb-1 text-matdash-muted">Action</label>
                  <select
                    value={actionType}
                    onChange={(e) => { setActionType(e.target.value); setPage(1); }}
                    className={`${inputCls} w-full`}
                  >
                    <option value="all">All actions</option>
                    {AUDIT_ACTION_TYPES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-matdash-muted">Actor</label>
                  <input
                    value={actor}
                    onChange={(e) => { setActor(e.target.value); setPage(1); }}
                    placeholder="Filter by actor"
                    className={`${inputCls} w-full`}
                  />
                </div>
                <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                  Clear filters
                </button>
              </div>
            </div>
          </div>

          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Column visibility">
              <Cog6ToothIcon className="h-4 w-4" />
            </button>
            <div className={dropdownPanel}>
              <div className={`w-52 py-2 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                {COLUMN_DEFS.map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                      dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleKeys.includes(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 text-matdash-primary"
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Export">
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Export</span>
            </button>
            <div className={dropdownPanel}>
              <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  type="button"
                  onClick={exportCsv}
                  className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                >
                  Export as CSV
                </button>
                <button
                  type="button"
                  onClick={exportJson}
                  className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                >
                  Export as JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
        {filtered.length} event{filtered.length === 1 ? '' : 's'}
      </div>

      <div className="overflow-x-auto">
        <table className="md-table min-w-full">
          <thead>
            <tr>
              {show('created_at') && <th>Time</th>}
              {show('action_type') && <th>Action</th>}
              {show('actor_name') && <th>Actor</th>}
              {show('resource') && <th>Resource</th>}
              {show('details') && <th>Details</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && pageRows.length === 0 && (
              <tr>
                <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                  No audit events match your filters.
                </td>
              </tr>
            )}
            {!loading && pageRows.map((row) => (
              <tr key={row.id}>
                {show('created_at') && (
                  <td className="text-matdash-muted whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                )}
                {show('action_type') && (
                  <td className="font-medium whitespace-nowrap">{row.action_type}</td>
                )}
                {show('actor_name') && (
                  <td className="whitespace-nowrap">{row.actor_name}</td>
                )}
                {show('resource') && (
                  <td className="text-matdash-muted whitespace-nowrap">{row.resource}</td>
                )}
                {show('details') && (
                  <td className="min-w-[220px]">{row.details}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
        <p className="text-xs text-matdash-muted mb-0">
          Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length}
        </p>
        <div className="flex items-center gap-2">
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
          >
            {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
          <span className="text-xs text-matdash-muted px-1">Page {page} of {totalPages}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLog;
